import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Select, Input, Button, Spin, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getProspectDiagnosisList, saveProspectDiagnosis } from 'api/insuranceBilling';
import useDiagnosisOptions from 'hooks/useDiagnosisOptions';
import { TProspectDiagnosis, TBillingCodeConfig } from 'types/insuranceBilling';
import emptyImg from 'assets/common/emptyBlue.svg';
import commonS from 'styles/common.module.less';
import s from './s.module.less';

type TDiagnosisEntry = {
    id: number;
    code?: string;
    description: string;
    isPrimary: boolean;
};

const MAX_DIAGNOSES = 12;

let nextId = 1;

type Props = {
    open: boolean;
    onClose: () => void;
    prospectId?: number;
};

const DiagnosisCodesModal = ({ open, onClose, prospectId }: Props) => {
    const [diagnosisOptions, loadingOptions] = useDiagnosisOptions();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [entries, setEntries] = useState<TDiagnosisEntry[]>([]);
    const [isEditing, setIsEditing] = useState(false);

    const fetchList = useCallback(async () => {
        if (!prospectId) return;
        setLoading(true);
        try {
            const res = await getProspectDiagnosisList(prospectId);
            if (res && !res.error) {
                const list: TProspectDiagnosis[] = res.data?.data || [];
                if (list.length > 0) {
                    const mapped = list.map((item) => ({
                        id: nextId++,
                        code: item.diagnosisCode,
                        description: item.description || '',
                        isPrimary: item.isPrimary,
                    }));
                    setEntries(mapped);
                    setIsEditing(true);
                } else {
                    setEntries([]);
                    setIsEditing(false);
                }
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [prospectId]);

    useEffect(() => {
        if (open) {
            fetchList();
        } else {
            setEntries([]);
            setIsEditing(false);
        }
    }, [open, fetchList]);

    const handleAddEntry = useCallback(() => {
        if (entries.length >= MAX_DIAGNOSES) return;
        const isPrimary = entries.length === 0;
        setEntries((prev) => [...prev, { id: nextId++, code: undefined, description: '', isPrimary }]);
    }, [entries.length]);

    const handleStartEditing = useCallback(() => {
        setIsEditing(true);
        setEntries([{ id: nextId++, code: undefined, description: '', isPrimary: true }]);
    }, []);

    const handleSelectCode = useCallback((entryId: number, value: string, options: TBillingCodeConfig[]) => {
        const matched = options.find((opt) => opt.code === value);
        if (matched) {
            setEntries((prev) =>
                prev.map((entry) =>
                    entry.id === entryId
                        ? { ...entry, code: matched.code, description: matched.name }
                        : entry,
                ),
            );
        }
    }, []);

    const handleRemove = useCallback((entryId: number) => {
        setEntries((prev) => {
            const next = prev.filter((e) => e.id !== entryId);
            if (next.length > 0 && !next.some((e) => e.isPrimary)) {
                next[0].isPrimary = true;
            }
            return next;
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!prospectId) return;

        const validEntries = entries.filter((e) => e.code);
        if (validEntries.length === 0) {
            message.error('Please add at least one diagnosis code');
            return;
        }

        const duplicates = validEntries.filter((e, i) => validEntries.findIndex((v) => v.code === e.code) !== i);
        if (duplicates.length > 0) {
            message.error('Duplicate diagnosis codes are not allowed');
            return;
        }

        setSaving(true);
        try {
            const data = validEntries.map((e, index) => ({
                diagnosisCode: e.code!,
                description: e.description || undefined,
                isPrimary: e.isPrimary,
                seq: index,
            }));
            const res = await saveProspectDiagnosis(prospectId, data);
            if (res && !res.error) {
                message.success('Diagnosis codes saved successfully');
                onClose();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            setSaving(false);
        }
    }, [prospectId, entries, onClose]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const renderEmptyState = () => (
        <div className={s.emptyState}>
            <div className={s.emptyIllustration}>
                <img src={emptyImg} alt="" width={200} height={200} />
            </div>
            <div className={s.emptyTitle}>No diagnosis codes yet</div>
            <div className={s.emptyDescription}>
                Add one here, or it will appear after the first visit where a diagnosis is recorded.
            </div>
            <Button type="primary" onClick={handleStartEditing}>
                Add diagnosis codes
            </Button>
        </div>
    );

    const renderEntry = (entry: TDiagnosisEntry, index: number) => {
        const selectedCodes = entries.filter((e) => e.id !== entry.id && e.code).map((e) => e.code);
        const filteredOptions = diagnosisOptions.filter((opt) => !selectedCodes.includes(opt.code));

        return (
            <div key={entry.id} className={s.codeCard}>
                <div className={s.cardBody}>
                    <div className={s.codeField}>
                        <div className={s.cardHeader}>
                            <span className={s.fieldLabel}>Diagnosis code</span>
                            <span className={index === 0 ? s.primaryTag : s.addOnTag}>
                                {index === 0 ? 'Primary' : 'Add-on'}
                            </span>
                        </div>
                        <Select
                            showSearch
                            value={entry.code}
                            placeholder="Search or select a diagnosis"
                            style={{ width: 300 }}
                            listHeight={200}
                            filterOption={(input, option) =>
                                String(option?.label).toLowerCase().includes(input.toLowerCase())
                            }
                            onSelect={(value: string) => handleSelectCode(entry.id, value, diagnosisOptions)}
                            options={filteredOptions.map((opt) => ({
                                label: `${opt.code} — ${opt.name}`,
                                value: opt.code,
                            }))}
                        />
                    </div>
                    <div className={s.descField}>
                        <div className={s.cardHeader}>
                            <span className={s.fieldLabel}>Description</span>
                        </div>
                        <Input
                            value={entry.description}
                            placeholder="None selected"
                            readOnly
                            style={{ width: 350 }}
                        />
                    </div>
                    <span className={s.deleteBtn} onClick={() => handleRemove(entry.id)}>
                        <DeleteOutlined />
                    </span>
                </div>
            </div>
        );
    };

    const renderEditState = () => (
        <div className={s.content}>
            {entries.map((entry, index) => renderEntry(entry, index))}
            <span
                className={`${s.addLink} ${entries.length >= MAX_DIAGNOSES ? s.disabled : ''}`}
                onClick={entries.length < MAX_DIAGNOSES ? handleAddEntry : undefined}
            >
                <PlusOutlined /> Add diagnosis
            </span>
        </div>
    );

    return (
        <Modal
            open={open}
            title="Diagnosis codes"
            onCancel={handleCancel}
            width={850}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
            className={`${s.modalWrap} ${commonS.modalFixHeightWrap}`}
            footer={
                isEditing
                    ? [
                        <Button key="cancel" onClick={handleCancel}>
                            Cancel
                        </Button>,
                        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                            Save
                        </Button>,
                    ]
                    : null
            }
        >
            <Spin spinning={loading || loadingOptions}>
                {isEditing ? renderEditState() : renderEmptyState()}
            </Spin>
        </Modal>
    );
};

export default DiagnosisCodesModal;
